import { caseInsensitiveSort } from '@/lib/sort';
import type { Group, State, LayGroupNode } from './types';
import { useMemo } from 'react';
import { memoize } from '@/lib/memoize';

function _buildLayGroupTree(data: Record<string, Group>) {
  const lookup: Record<string, LayGroupNode> = {};
  const root: Group[] = [];
  for (const id in data) {
    const node = makeLayGroupNode(data, id, lookup);
    if (node.item.data.parent_id) {
      node.parent = makeLayGroupNode(data, node.item.data.parent_id, lookup);
      if (!node.parent.children) {
        node.parent.children = [];
      }

      node.parent.children.push(node);
    }
  }

  return { root, lookup };
}

function makeLayGroupNode(
  data: Record<string, Group>,
  id: string,
  lookup: Record<string, LayGroupNode>,
) {
  let node = lookup[id];
  if (node) {
    return node;
  }

  const item = data[id];
  node = {
    item,
    children: null,
    parent: null,
  };

  lookup[item.id] = node;

  return node;
}

const comparator = caseInsensitiveSort<Group>(['data', 'title']);

function allGroupsList(data: Group[]) {
  return data.slice().sort(comparator);
}

export function activeGroupsList(data: Group[]) {
  return allGroupsList(data)
    .filter((item) => !item.data.archived)
    .sort(caseInsensitiveSort('title'));
}

export function archiveGroupsList(data: Group[]) {
  return allGroupsList(data)
    .filter((item) => item.data.archived)
    .sort(caseInsensitiveSort('title'));
}

export function useComputed(state: State) {
  const groupLookup = useMemo(() => memoize(state.data), [state.data]);
  const _allGroupsList = useMemo(() => allGroupsList(state.data), [state.data]);
  const _activeGroupsList = useMemo(
    () => activeGroupsList(state.data),
    [state.data],
  );
  const _archivedGroupsList = useMemo(
    () => archiveGroupsList(state.data),
    [state.data],
  );
  // layGroupTree: useMemo(
  //   () => buildLayGroupTree(context.state.layGroups.data),
  //   [context.state.layGroups.data],
  // ),

  return useMemo(
    () => ({
      groupLookup,
      allGroupsList: _allGroupsList,
      activeGroupsList: _activeGroupsList,
      archivedGroupsList: _archivedGroupsList,
    }),
    [groupLookup, _allGroupsList, _activeGroupsList, _archivedGroupsList],
  );
}

export type Computed = ReturnType<typeof useComputed>;
